import { call, put } from "redux-saga/effects";
import { requestSystemConfiguration, requestConfigValue, requestSaveConfig, requestBackupConfig, requestRestoreConfig, requestTestAIConnection, requestAiUsage, requestTestHubspotConnection, requestTestIntuitConnection, requestWhatsAppStatus, requestWhatsAppQr, requestStartWhatsAppSession, requestLogoutWhatsAppSession, requestTestWhatsAppMessage } from "../requests/config";
import { setConfig } from "../../slices/config";
import { showAlert } from '../../slices/alerts';

export function* handleSystemConfiguration(action) {
    try {
        const response = yield call(requestSystemConfiguration);
        const { data } = response;
        if (data !== undefined) {
            const { value } = data;
            yield put(setConfig({ config: value }));
        }
    } catch (error) {
        const errorMessage = error?.response?.data?.message || error?.response?.data?.msg || error?.message || 'Failed to load system configuration';
        yield put(showAlert({ severity: "error", message: errorMessage }))
    }
}

export function* handleConfigValue(action) {
    try {
        const { key, value, callback } = action.payload;
        const response = yield call(requestConfigValue, key, value);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        const { callback, silent } = action.payload || {};
        const notConfigured = error?.response?.status === 404;
        if (!silent && !notConfigured) {
            const errorMessage = error?.response?.data?.message || error?.response?.data?.msg || error?.message || 'Failed to load configuration value';
            yield put(showAlert({ severity: "error", message: errorMessage }));
        }
        if (callback) {
            callback(null);
        }
    }
}

export function* handleSaveConfig(action) {
    try {
        const { key, value, field, callback } = action.payload;
        const response = yield call(requestSaveConfig, key, value, field);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        const errorMessage = error?.response?.data?.message || error?.response?.data?.msg || error?.message || 'Failed to save configuration';
        yield put(showAlert({ severity: "error", message: errorMessage }))
        if (action.payload?.callback) {
            action.payload.callback({ status: 0, msg: errorMessage });
        }
    }
}

export function* handleBackupConfig(action) {
    try {
        const { callback } = action.payload || {};
        const response = yield call(requestBackupConfig);
        if (callback) {
            callback({ ok: true, data: response.data });
        }
    } catch (error) {
        let errorMessage = error?.message || 'Failed to create settings backup';
        const data = error?.response?.data;
        if (data instanceof Blob) {
            try {
                const parsed = JSON.parse(yield call([data, data.text]));
                errorMessage = parsed.message || parsed.msg || errorMessage;
            } catch {
                // keep the fallback message
            }
        } else {
            errorMessage = data?.message || data?.msg || errorMessage;
        }
        yield put(showAlert({ severity: "error", message: errorMessage }));
        const { callback } = action.payload || {};
        if (callback) {
            callback(null);
        }
    }
}

export function* handleRestoreConfig(action) {
    try {
        const { data, callback } = action.payload;
        const response = yield call(requestRestoreConfig, data);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        const errorMessage = error?.response?.data?.message || error?.response?.data?.msg || error?.message || 'Failed to restore settings';
        yield put(showAlert({ severity: "error", message: errorMessage }));
        const { callback } = action.payload || {};
        if (callback) {
            callback(null);
        }
    }
}

export function* handleTestAIConnection(action) {
    try {
        const { containerName, model, provider, callback } = action.payload;
        const response = yield call(requestTestAIConnection, containerName, model, provider);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        const errorMessage = error?.response?.data?.message || error?.response?.data?.msg || error?.message || 'Failed to test AI connection';
        yield put(showAlert({ severity: "error", message: errorMessage }))
        if (action.payload?.callback) {
            action.payload.callback({ success: false, message: errorMessage });
        }
    }
}

export function* handleGetAiUsage(action) {
    try {
        const { days, callback } = action.payload || {};
        const response = yield call(requestAiUsage, days);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        const errorMessage = error?.response?.data?.message || error?.response?.data?.msg || error?.message || 'Failed to load AI usage';
        yield put(showAlert({ severity: "error", message: errorMessage }));
        if (action.payload?.callback) {
            action.payload.callback(null);
        }
    }
}

export function* handleTestHubspotConnection(action) {
    const { callback } = action.payload || {};
    try {
        const response = yield call(requestTestHubspotConnection);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        const errorMessage = error?.response?.data?.message || error?.response?.data?.msg || error?.message || 'Failed to test HubSpot connection';
        yield put(showAlert({ severity: "error", message: errorMessage }))
        if (callback) {
            callback({ ok: false, message: errorMessage });
        }
    }
}

export function* handleTestIntuitConnection(action) {
    const { callback } = action.payload || {};
    try {
        const response = yield call(requestTestIntuitConnection);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        const errorMessage = error?.response?.data?.message || error?.response?.data?.msg || error?.message || 'Failed to test Intuit connection';
        yield put(showAlert({ severity: "error", message: errorMessage }))
        if (callback) {
            callback({ ok: false, message: errorMessage });
        }
    }
}

export function* handleGetWhatsAppStatus(action) {
    const { callback, silent } = action.payload || {};
    try {
        const response = yield call(requestWhatsAppStatus);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        const notDeployed = error?.response?.status === 404;
        const errorMessage = notDeployed
            ? 'This server does not have the WhatsApp gateway routes yet.'
            : error?.response?.data?.message || error?.response?.data?.msg || error?.message || 'Could not reach the WhatsApp gateway status endpoint';
        if (!silent) {
            yield put(showAlert({ severity: "error", message: errorMessage }));
        }
        if (callback) {
            callback(null, errorMessage);
        }
    }
}

export function* handleGetWhatsAppQr(action) {
    const { callback, silent } = action.payload || {};
    try {
        const response = yield call(requestWhatsAppQr);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        const errorMessage = error?.response?.data?.message || error?.response?.data?.msg || error?.message || 'Could not load the pairing QR code';
        if (!silent) {
            yield put(showAlert({ severity: "error", message: errorMessage }));
        }
        if (callback) {
            callback(null, errorMessage);
        }
    }
}

export function* handleStartWhatsAppSession(action) {
    const { callback } = action.payload || {};
    try {
        const response = yield call(requestStartWhatsAppSession);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        const errorMessage = error?.response?.data?.message || error?.response?.data?.msg || error?.message || 'Failed to start the WhatsApp session';
        yield put(showAlert({ severity: "error", message: errorMessage }));
        if (callback) {
            callback(null, errorMessage);
        }
    }
}

export function* handleLogoutWhatsAppSession(action) {
    const { callback } = action.payload || {};
    try {
        const response = yield call(requestLogoutWhatsAppSession);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        const errorMessage = error?.response?.data?.message || error?.response?.data?.msg || error?.message || 'Failed to disconnect the WhatsApp session';
        yield put(showAlert({ severity: "error", message: errorMessage }));
        if (callback) {
            callback(null, errorMessage);
        }
    }
}

export function* handleTestWhatsAppMessage(action) {
    const { phone, callback } = action.payload || {};
    try {
        const response = yield call(requestTestWhatsAppMessage, phone);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        const errorMessage = error?.response?.data?.message || error?.response?.data?.msg || error?.message || 'Failed to send the test message';
        yield put(showAlert({ severity: "error", message: errorMessage }));
        if (callback) {
            callback(null, errorMessage);
        }
    }
}